import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { CheckoutResponse } from '../../types/reservation';
import { makeT, PublicReservationLang } from '../../i18n/publicReservationI18n';

/**
 * 予約デポジット決済の決済ステップ（Phase 2・3DS・統合契約 B2）。
 *
 * checkout 応答（clientSecret / stripeAccountId）を受け取り、
 * `loadStripe(pk, { stripeAccount })`（Connect Direct Charge のため接続アカウントを指定）で
 * Stripe.js を初期化し、`<Elements options={{ clientSecret }}>` + `<PaymentElement>` を表示する。
 * 「支払う」で `stripe.confirmPayment` を呼ぶ。3DS が必要な場合は Stripe.js が return_url へ
 * リダイレクトし、復帰後に呼び出し側（PublicReservation）が finalize をポーリングする。
 * リダイレクト不要（非3DS）の成功時は onConfirmed で確定へ進める。
 *
 * clientSecret は Redux/localStorage に保持せず、この props 経由でのみ Stripe.js に渡す。
 * pk は環境変数（プラットフォームの公開可能キー）を使う。
 */

// プラットフォームの公開可能キー（stg はテストキー、prod は封印中のため空）。
const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

/**
 * loadStripe は (pk, stripeAccount) の組ごとにキャッシュする。
 * 接続アカウントが変わると別インスタンスが必要なため account をキーに含める。
 */
const stripePromiseCache = new Map<string, Promise<Stripe | null>>();
const getStripePromise = (stripeAccount: string): Promise<Stripe | null> => {
  const key = `${PUBLISHABLE_KEY}::${stripeAccount}`;
  let cached = stripePromiseCache.get(key);
  if (!cached) {
    cached = PUBLISHABLE_KEY
      ? loadStripe(PUBLISHABLE_KEY, { stripeAccount })
      : Promise.resolve(null);
    stripePromiseCache.set(key, cached);
  }
  return cached;
};

interface PaymentFormProps {
  lang: PublicReservationLang;
  returnUrl: string;
  /** 非3DS（リダイレクト不要）で即時成功したときに呼ぶ。3DS 時は return_url へ遷移するため呼ばれない。 */
  onConfirmed: () => void;
  onBack?: () => void;
}

/** Elements 配下でのみ使えるフォーム本体（useStripe/useElements のため分離） */
const PaymentForm: React.FC<PaymentFormProps> = ({
  lang,
  returnUrl,
  onConfirmed,
  onBack,
}) => {
  const t = useMemo(() => makeT(lang), [lang]);
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    // confirmParams.return_url へは 3DS 等でリダイレクトが必要な場合のみ遷移する。
    // 不要な場合は redirect:'if_required' により遷移せず paymentIntent が返る。
    // clientSecret は <Elements options={{ clientSecret }}> 側で渡しているため、ここでは渡さない。
    // elements と clientSecret を同時に渡すと Stripe.js が deferred フローとみなし
    // 「elements.submit() must be called before confirmPayment」で失敗する。
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    if (result.error) {
      // カードエラー・検証エラー等。ユーザーに再試行を促す（金額は失効しない）。
      setError(result.error.message || t('paymentFailedRetry'));
      setSubmitting(false);
      return;
    }
    const status = result.paymentIntent?.status;
    if (status === 'succeeded' || status === 'requires_capture' || status === 'processing') {
      // 非3DS成功。finalize は呼び出し側で行う。
      onConfirmed();
      return;
    }
    // ここに来るのは想定外（requires_action は redirect で処理済みのはず）。再試行させる。
    setError(t('paymentFailedRetry'));
    setSubmitting(false);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('paymentStepGuide')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <PaymentElement />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button disabled={submitting || !onBack} onClick={onBack}>
          {t('back')}
        </Button>
        <Button
          variant="contained"
          disabled={!stripe || !elements || submitting}
          onClick={handlePay}
        >
          {submitting ? t('paying') : t('payButton')}
        </Button>
      </Box>
    </Box>
  );
};

interface ReservationPaymentStepProps {
  lang: PublicReservationLang;
  checkout: CheckoutResponse;
  returnUrl: string;
  onConfirmed: () => void;
  onBack?: () => void;
}

const ReservationPaymentStep: React.FC<ReservationPaymentStepProps> = ({
  lang,
  checkout,
  returnUrl,
  onConfirmed,
  onBack,
}) => {
  const t = useMemo(() => makeT(lang), [lang]);
  const stripePromise = useMemo(
    () => getStripePromise(checkout.stripeAccountId),
    [checkout.stripeAccountId],
  );

  // pk 未設定（prod 封印中など）の場合は決済フォームを出さずエラー表示にする。
  if (!PUBLISHABLE_KEY) {
    return <Alert severity="error">{t('paymentLoadError')}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('paymentStepTitle')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          py: 1,
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('depositAmountLabel')}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {`¥${checkout.amount.toLocaleString()}`}
        </Typography>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }} icon={false}>
        {t('paymentExpiryNote')}
      </Alert>
      <Elements
        stripe={stripePromise}
        options={{ clientSecret: checkout.clientSecret }}
      >
        <React.Suspense
          fallback={
            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Box>
          }
        >
          <PaymentForm
            lang={lang}
            returnUrl={returnUrl}
            onConfirmed={onConfirmed}
            onBack={onBack}
          />
        </React.Suspense>
      </Elements>
    </Box>
  );
};

export default ReservationPaymentStep;
